import { useState, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import type { Store, BadgeType } from '@/types'
import { Badge } from '@/components/ui/Badge'
import { StatusBadge } from '@/components/ui/StatusBadge'
import { MapPinIcon, PhoneIcon, ChevronDownIcon, ClockIcon, WalkingIcon, TruckIcon } from '@/components/ui/icons'
import { getStorePickupStatus, getStoreDeliveryStatus } from '@/utils/storePickupStatus'
import { formatDistance, getStorePhotoUrl } from '@/utils/formatters'
import { redirectToStore, type DeliveryAddressParams } from '@/utils/storeRedirect'
import { selectFulfillmentMode } from '@/App/fulfillmentModeSlice'

interface StoreCardProps {
  store: Store
  variant: 'pickup' | 'delivery'
  badges?: BadgeType[]
  onSelect?: (store: Store) => void
  disabled?: boolean
  showDetails?: boolean
  /** If true, preloads the image immediately instead of lazy loading */
  preloadImage?: boolean
  deliveryAddress?: DeliveryAddressParams | null
}

export function StoreCard({
  store,
  variant,
  badges,
  onSelect,
  disabled = false,
  showDetails = false,
  preloadImage = false,
  deliveryAddress,
}: StoreCardProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [imageLoaded, setImageLoaded] = useState(false)
  const [imageError, setImageError] = useState(false)
  const displayBadges = badges || store.badges || []

  const fulfillmentMode = useSelector(selectFulfillmentMode)

  const photoUrl = useMemo(() => getStorePhotoUrl(store.storePhotoPath, store.logoPath), [store.storePhotoPath, store.logoPath])
  const storeNameTitleCase = useMemo(() => {
    if (!store.name) return ''
    return store.name
      .toLowerCase()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }, [store.name])

  const pickupStatus = useMemo(
    () => (variant === 'pickup' ? getStorePickupStatus(store) : null),
    [store, variant],
  )

  const deliveryStatus = useMemo(
    () => (variant === 'delivery' ? getStoreDeliveryStatus(store) : null),
    [store, variant],
  )

  // Disabled if explicitly passed or if not available for the current mode
  const isDisabled = useMemo(() => {
    if (disabled) return true
    if (variant === 'pickup') return pickupStatus != null && !pickupStatus.isAvailableForPickup
    if (variant === 'delivery') return deliveryStatus != null && !deliveryStatus.isAvailableForDelivery
    return false
  }, [disabled, variant, pickupStatus, deliveryStatus])

  // The status badge to show (pickup or delivery)
  const activeStatus = variant === 'delivery' ? deliveryStatus : pickupStatus

  useEffect(() => {
    if (preloadImage && photoUrl && !imageLoaded && !imageError) {
      const img = new Image()
      img.src = photoUrl
      img.onload = () => setImageLoaded(true)
      img.onerror = () => setImageError(true)
    }
  }, [preloadImage, photoUrl, imageLoaded, imageError])

  const handleExpandClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsExpanded(!isExpanded)
  }

  const handleSelect = () => {
    if (navigator.vibrate) {
      navigator.vibrate(15)
    }
    if (onSelect) {
      onSelect(store)
    } else {
      redirectToStore({
        store,
        fulfillmentMode,
        deliveryAddress,
      })
    }
  }

  return (
    <article
      className={`
        group w-full rounded-2xl overflow-hidden
        bg-surface-elevated
        border border-border-light
        shadow-card
        transition-all duration-300 ease-in-out
        hover:shadow-card-hover hover:border-border-default hover:-translate-y-0.5
        ${isDisabled ? 'opacity-70 contrast-[0.85]' : ''}
        @container
      `}
    >
      <button
        onClick={handleSelect}
        aria-label={`${storeNameTitleCase}, ${store.area}`}
        className={`
          w-full text-left
          flex flex-col @md:flex-row
          active:scale-[0.98] transition-transform duration-150
          focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2
          ${isDisabled ? 'cursor-pointer' : ''}
        `}
      >
        {/* ── Image Section (shown for both pickup & delivery) ── */}
        <div className="relative @md:w-56 @md:shrink-0 h-48 @md:h-auto @md:min-h-[200px] bg-surface-tertiary overflow-hidden">
          {/* Loading placeholder pulse */}
          {!imageLoaded && photoUrl && !imageError && (
            <div className="absolute inset-0 animate-pulse bg-surface-tertiary" />
          )}

          {photoUrl && !imageError ? (
            <img
              src={photoUrl}
              alt={`${storeNameTitleCase} location`}
              className={`
                w-full h-full object-cover
                transition-transform duration-500 group-hover:scale-105
                ${isDisabled ? 'grayscale' : ''}
                ${imageLoaded ? 'opacity-100' : 'opacity-0'}
              `}
              loading={preloadImage ? 'eager' : 'lazy'}
              fetchPriority={preloadImage ? 'high' : 'auto'}
              onLoad={() => setImageLoaded(true)}
              onError={() => setImageError(true)}
            />
          ) : (
            <StoreMapPlaceholder
              lat={store.addressLocation?.lat ?? 0}
              lon={store.addressLocation?.long ?? 0}
              disabled={isDisabled}
            />
          )}

          {/* Badge overlays – mobile only (stacked for better visibility) */}
          <div className="@md:hidden absolute inset-x-0 top-0 p-3 flex justify-between items-start">
            <div className="flex flex-wrap gap-1.5 focus-within:z-10">
              {displayBadges.map((badge) => (
                <Badge key={badge} type={badge} size="sm" className="shadow-sm backdrop-blur-md bg-opacity-90" />
              ))}
            </div>
            {activeStatus && (
              <div className="shrink-0">
                <StatusBadge status={activeStatus.status} size="sm" className="shadow-sm backdrop-blur-md bg-opacity-90" />
              </div>
            )}
          </div>

          {/* Gradient overlay for mobile text legibility (more pronounced) */}
          <div className="@md:hidden absolute inset-x-0 bottom-0 h-24 bg-linear-to-t from-black/60 via-black/20 to-transparent" />
        </div>

        {/* ── Content Section ── */}
        <div className="flex-1 p-5 @md:py-5 @md:px-6 min-w-0 flex flex-col justify-between">
          <div className="flex flex-col gap-1.5">
            {/* Header row – name + desktop status badge */}
            <div className="flex items-start justify-between gap-3 mb-1">
              <div className="flex-1 min-w-0">
                <div className="flex flex-col gap-1">
                  <h3 className="font-bold text-lg/6 text-text-primary group-hover:text-brand-primary transition-colors truncate">
                    {storeNameTitleCase}
                  </h3>
                  {/* Desktop custom badges */}
                  <div className="hidden @md:flex flex-wrap gap-1.5 mt-0.5">
                    {displayBadges.map((badge) => (
                      <Badge key={badge} type={badge} size="sm" />
                    ))}
                  </div>
                </div>
              </div>

              {/* Desktop status badge */}
              {activeStatus && (
                <div className="hidden @md:block shrink-0">
                  <StatusBadge status={activeStatus.status} size="sm" />
                </div>
              )}
            </div>

            {/* Address */}
            <p className="text-sm/5 text-text-secondary truncate mb-1.5">
              {store.street}, {store.area}
            </p>

            {/* ── Pickup-specific metadata ── */}
            {variant === 'pickup' && (
              <div className="flex flex-wrap items-center gap-y-2 gap-x-4 text-sm/5 text-text-secondary">
                {store.distance !== undefined && (
                  <span className="flex items-center gap-1.5 bg-surface-secondary/50 px-2 py-0.5 rounded-full border border-border-light">
                    <WalkingIcon className="size-3.5 text-text-tertiary" />
                    {formatDistance(store.distance)}
                  </span>
                )}
                {store.travelTime !== undefined && (
                  <span className="flex items-center gap-1.5 bg-surface-secondary/50 px-2 py-0.5 rounded-full border border-border-light">
                    <ClockIcon className="size-3.5 text-text-tertiary" />
                    {store.travelTime} min
                  </span>
                )}
                {pickupStatus?.closingTime && (
                  <span className="flex items-center gap-1.5">
                    <span className={`size-1.5 rounded-full ${pickupStatus.status === 'closing_soon' ? 'bg-status-closing' : 'bg-status-open'}`} />
                    {pickupStatus.status === 'closing_soon'
                      ? `Closes at ${pickupStatus.closingTime}`
                      : `Open till ${pickupStatus.closingTime}`}
                  </span>
                )}
              </div>
            )}

            {/* ── Delivery-specific chips ── */}
            {variant === 'delivery' && (
              <div className="flex flex-wrap items-center gap-2 mt-0.5">
                {/* Distance from delivery address */}
                {store.distance !== undefined && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs/4 font-semibold bg-brand-primary/5 text-brand-primary border border-brand-primary/10">
                    {formatDistance(store.distance)}
                  </span>
                )}

                {/* Delivery cost */}
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs/4 font-medium bg-surface-secondary text-text-primary border border-border-default">
                  <TruckIcon className="size-3.5 text-text-tertiary" />
                  {store.deliveryCost && parseFloat(store.deliveryCost) === 0
                    ? 'Free Delivery'
                    : store.deliveryCost
                    ? `${store.currency ?? '$'}${parseFloat(store.deliveryCost).toFixed(2)}`
                    : 'Delivery varies'}
                </span>

                {/* Min order */}
                {store.minOrderValue && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs/4 font-medium bg-surface-secondary text-text-primary border border-border-default">
                    Min. {store.currency ?? '$'}{parseFloat(store.minOrderValue).toFixed(2)}
                  </span>
                )}
              </div>
            )}
          </div>


          {/* Next-open hint for pickup */}
          {variant === 'pickup' &&
            pickupStatus &&
            !pickupStatus.isAvailableForPickup &&
            pickupStatus.nextOpenTime && (
              <div className="mt-3 flex items-center gap-2 px-3 py-2 rounded-lg bg-status-opens-soon/10 text-status-opens-soon border border-status-opens-soon/20 animate-fade-in">
                <ClockIcon className="size-4 shrink-0" />
                <span className="text-xs font-semibold">{pickupStatus.nextOpenTime}</span>
              </div>
            )}

          {/* Next-open hint for delivery */}
          {variant === 'delivery' &&
            deliveryStatus &&
            !deliveryStatus.isAvailableForDelivery &&
            deliveryStatus.nextOpenTime && (
              <div className="mt-3 flex items-center gap-2 px-3 py-2 rounded-lg bg-status-opens-soon/10 text-status-opens-soon border border-status-opens-soon/20 animate-fade-in">
                <ClockIcon className="size-4 shrink-0" />
                <span className="text-xs font-semibold">{deliveryStatus.nextOpenTime}</span>
              </div>
            )}
        </div>
      </button>

      {/* ── Action Bar ── */}
      <div className="flex items-center justify-between px-5 py-3 border-t border-border-light bg-surface-secondary/30 backdrop-blur-sm">
        <div className="flex items-center gap-4">
          {store.phoneNumber && (
            <a
              href={`tel:${store.phoneNumber}`}
              onClick={(e) => e.stopPropagation()}
              aria-label={`Call ${storeNameTitleCase}`}
              className="flex items-center gap-2 text-sm font-medium text-text-secondary hover:text-brand-primary transition-colors group/tel"
            >
              <div className="p-2 rounded-full bg-surface-tertiary group-hover:tel:bg-brand-primary/10 transition-colors">
                <PhoneIcon className="size-4" />
              </div>
              <span className="hidden @sm:inline">Call Store</span>
            </a>
          )}
        </div>

        {/* Expand Details – pickup only */}
        {showDetails && variant === 'pickup' && (
          <button
            onClick={handleExpandClick}
            aria-expanded={isExpanded}
            aria-label={isExpanded ? 'Hide details' : 'Show details'}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-semibold text-brand-primary hover:bg-brand-primary/5 transition-all"
          >
            <span>{isExpanded ? 'Less info' : 'More info'}</span>
            <ChevronDownIcon className={`size-4 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} />
          </button>
        )}
      </div>

      {/* ── Expanded Details – pickup only ── */}
      {showDetails && variant === 'pickup' && isExpanded && (
        <div className="px-4 pb-4 border-t border-border-light animate-slide-up">
          {/* Hours */}
          {store.shifts && (
            <div className="mt-3">
              <h4 className="text-xs/4 font-semibold text-text-secondary uppercase tracking-wide mb-2">Hours</h4>
              <div className="grid grid-cols-2 gap-1 text-sm/5">
                {Object.entries(store.shifts).map(([day, shift]: [string, any]) => (
                  <div key={day} className="flex justify-between">
                    <span className="text-text-secondary capitalize">{day}</span>
                    <span className="text-text-primary">{shift.start} – {shift.end}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Full Address */}
          <div className="mt-3">
            <h4 className="text-xs/4 font-semibold text-text-secondary uppercase tracking-wide mb-1">Address</h4>
            <p className="text-sm/5 text-text-primary">
              {store.street}
              <br />
              {store.area}, {store.state} {store.postalCode}
            </p>
          </div>

          {/* Directions */}
          <a
            href={`https://www.google.com/maps/dir/?api=1&destination=${store.addressLocation?.lat},${store.addressLocation?.long}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="mt-3 w-full py-2.5 flex items-center justify-center gap-2 text-sm/5 font-medium text-brand-primary bg-brand-primary/5 rounded-lg hover:bg-brand-primary/10 transition-colors"
          >
            <MapPinIcon className="size-4" />
            Get Directions
          </a>
        </div>
      )}
    </article>
  )
}

// ─────────────────────────────────────────────
// Map placeholder when no store photo exists
// ─────────────────────────────────────────────

interface StoreMapPlaceholderProps {
  lat: number
  lon: number
  disabled?: boolean
}

function StoreMapPlaceholder({ lat, lon, disabled }: StoreMapPlaceholderProps) {
  const hue = Math.abs((lat * lon) % 360)

  return (
    <div
      className={`w-full h-full relative ${disabled ? 'grayscale' : ''}`}
      style={{
        background: `linear-gradient(135deg,
          hsl(${hue}, 20%, 92%) 0%,
          hsl(${hue + 20}, 15%, 88%) 50%,
          hsl(${hue + 40}, 20%, 85%) 100%
        )`,
      }}
    >
      <div
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage: `
            linear-gradient(to right, #94a3b8 1px, transparent 1px),
            linear-gradient(to bottom, #94a3b8 1px, transparent 1px)
          `,
          backgroundSize: '24px 24px',
        }}
      />

      <svg className="absolute inset-0 w-full h-full opacity-40" preserveAspectRatio="none">
        <line x1="0" y1="50%" x2="100%" y2="55%" stroke="#94a3b8" strokeWidth="3" />
        <line x1="40%" y1="0" x2="45%" y2="100%" stroke="#94a3b8" strokeWidth="2" />
      </svg>

      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-full">
        <svg className="w-8 h-10 drop-shadow-lg text-brand-primary" viewBox="0 0 24 30" fill="currentColor">
          <path d="M12 0C5.373 0 0 5.373 0 12c0 9 12 18 12 18s12-9 12-18c0-6.627-5.373-12-12-12z" />
          <circle cx="12" cy="12" r="5" fill="white" />
        </svg>
      </div>
    </div>
  )
}
